import { useEffect, useMemo, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import type {
  ArtworkListItem,
  IIAFilterOptions,
  MarketplaceListPageData,
} from '@/types/artwork'

/*
  Figma: node 249:1934 (Marketplace list)
  - Page heading: "Marketplace", h1-title (180px, alfarn-2, weight 700)
  - 2-column layout: left filter panel (340px), right 3-column artwork grid
  - Search box, Refine By active-filters, and expandable filter categories:
    Artist, Style, Palette, Mood, Genre, Price (range), Size, Medium,
    Orientation, Framing.
  - Artwork card: 1:1 image + name (h2-small 28px alfarn-2) + artist name +
    price (Poppins Light 32px)

  Filter categories that map to custom fields (per the task brief):
    Artist, Style, Palette, Mood, Price, Medium, Orientation
*/

/* ---------- Helpers ---------- */

function formatPrice(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return ''
  const rounded = Math.round(price)
  return `$${rounded.toLocaleString()}`
}

function arrayHasAny(values: string[] | null | undefined, selected: Set<string>): boolean {
  if (!values || values.length === 0) return false
  for (const v of values) {
    if (selected.has(v)) return true
  }
  return false
}

/* ---------- Filter state ---------- */

interface FilterState {
  artistIds: Set<string>
  styles: Set<string>
  palettes: Set<string>
  moods: Set<string>
  mediums: Set<string>
  orientations: Set<string>
  sizes: Set<string>
  priceMin: number
  priceMax: number
}

function emptyFilters(priceMax: number): FilterState {
  return {
    artistIds: new Set(),
    styles: new Set(),
    palettes: new Set(),
    moods: new Set(),
    mediums: new Set(),
    orientations: new Set(),
    sizes: new Set(),
    priceMin: 0,
    priceMax,
  }
}

/* ---------- Derive filter options ----------
   Filter options come from TWO sources:
   1. IIA custom field definitions (admin_tables query) — the master list of
      options for Style, Palette, Mood, Medium, Orientation. Adding a new
      option in IIA automatically adds it to the front-end filters.
   2. Product data — used for Artist filter (counts how many artworks per artist).
*/
interface DerivedOptions {
  artists: Array<{ id: string; name: string; slug: string; count: number }>
  styles: string[]
  palettes: string[]
  moods: string[]
  mediums: string[]
  orientations: string[]
  sizes: string[]
}

function deriveOptions(artworks: ArtworkListItem[], iiaOptions: IIAFilterOptions): DerivedOptions {
  // Artists are derived from product data (with counts)
  const artistMap = new Map<string, { name: string; slug: string; count: number }>()
  for (const a of artworks) {
    if (a.artist_id && a.artist_name) {
      const existing = artistMap.get(a.artist_id)
      if (existing) {
        existing.count += 1
      } else {
        artistMap.set(a.artist_id, {
          name: a.artist_name,
          slug: a.artist_slug ?? '',
          count: 1,
        })
      }
    }
  }

  const artistsArr = Array.from(artistMap.entries())
    .map(([id, v]) => ({ id, name: v.name, slug: v.slug, count: v.count }))
    .sort((x, y) => x.name.localeCompare(y.name))

  // All other filters come from IIA custom field definitions
  return {
    artists: artistsArr,
    styles: iiaOptions.style ?? [],
    palettes: iiaOptions.palette ?? [],
    moods: iiaOptions.mood ?? [],
    mediums: iiaOptions.medium ?? [],
    orientations: iiaOptions.orientation ?? [],
    sizes: iiaOptions.size ?? [],
  }
}

/* ---------- Apply filters + search ---------- */
function filterArtworks(
  artworks: ArtworkListItem[],
  filters: FilterState,
  search: string,
): ArtworkListItem[] {
  const q = search.trim().toLowerCase()
  return artworks.filter((a) => {
    // Hide sold items? No — the task spec shows artworks in all statuses; sold
    // artworks are still listed and shown in detail view with a SOLD watermark.

    // Search: name, artist name, short description
    if (q) {
      const hay = `${a.name} ${a.artist_name ?? ''} ${a.short_description ?? ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }

    if (filters.artistIds.size > 0 && (!a.artist_id || !filters.artistIds.has(a.artist_id))) {
      return false
    }
    if (filters.styles.size > 0 && !arrayHasAny(a.style, filters.styles)) return false
    if (filters.palettes.size > 0 && !arrayHasAny(a.palette, filters.palettes)) return false
    if (filters.moods.size > 0 && !arrayHasAny(a.mood, filters.moods)) return false
    if (filters.mediums.size > 0 && !arrayHasAny(a.medium, filters.mediums)) return false
    if (filters.orientations.size > 0 && (!a.orientation || !filters.orientations.has(a.orientation))) {
      return false
    }
    if (filters.sizes.size > 0 && (!a.size || !filters.sizes.has(a.size))) {
      return false
    }

    const price = typeof a.price === 'number' ? a.price : null
    if (price != null) {
      if (price < filters.priceMin) return false
      if (price > filters.priceMax) return false
    }

    return true
  })
}

/* ============================== COMPONENTS ============================== */

function ChevronDown({ open }: { open: boolean }) {
  // Closed = points up (^), Open = rotated 180deg points down (v)
  // Exact SVG path from Figma node 255:333 (arrow-icon)
  return (
    <span aria-hidden="true" className="mig-marketplace-chevron" data-open={open}>
      <svg viewBox="0 0 27.912 16.419" width="28" height="16" fill="currentColor">
        <path d="M13.956 7.966L6.771 16.419H0L13.956 0L27.912 16.419H21.141L13.956 7.966Z" />
      </svg>
    </span>
  )
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span aria-hidden="true" className="mig-marketplace-checkbox" data-checked={checked}>
      {checked && (
        <svg
          viewBox="0 0 12 12"
          width="12"
          height="12"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2"
          className="mig-marketplace-checkbox-check"
        >
          <path d="M2 6.5 L5 9.5 L10 3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

function Divider() {
  return <div className="mig-divider-line" />
}

/* ---------- Search Box ---------- */
function SearchBox({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="mig-marketplace-search">
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" stroke="#FFFFFF" strokeWidth="1.5" aria-hidden="true" className="mig-marketplace-search-icon">
        <circle cx="7" cy="7" r="5.5" />
        <path d="M11 11 L16 16" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search"
        aria-label="Search artworks"
        className="mig-marketplace-search-input"
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="mig-marketplace-search-clear"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
            <path d="M1 1 L11 11 M11 1 L1 11" />
          </svg>
        </button>
      )}
    </div>
  )
}

/* ---------- Refine By (active filter chips) ---------- */
function RefineBy({
  filters,
  options,
  priceMaxCap,
  onRemove,
  onClearAll,
  mobileOpen,
  setMobileOpen,
}: {
  filters: FilterState
  options: DerivedOptions
  priceMaxCap: number
  onRemove: (action: () => void) => void
  onClearAll: () => void
  mobileOpen: boolean
  setMobileOpen: (v: boolean) => void
}) {
  const items: Array<{ label: string; value: string; remove: () => void }> = []

  filters.artistIds.forEach((id) => {
    const a = options.artists.find((x) => x.id === id)
    if (a) items.push({ label: 'Artist', value: a.name, remove: () => filters.artistIds.delete(id) })
  })
  filters.styles.forEach((s) =>
    items.push({ label: 'Style', value: s, remove: () => filters.styles.delete(s) }),
  )
  filters.palettes.forEach((p) =>
    items.push({ label: 'Palette', value: p, remove: () => filters.palettes.delete(p) }),
  )
  filters.moods.forEach((m) =>
    items.push({ label: 'Mood', value: m, remove: () => filters.moods.delete(m) }),
  )
  filters.mediums.forEach((m) =>
    items.push({ label: 'Medium', value: m, remove: () => filters.mediums.delete(m) }),
  )
  filters.orientations.forEach((o) =>
    items.push({ label: 'Orientation', value: o, remove: () => filters.orientations.delete(o) }),
  )
  filters.sizes.forEach((s) =>
    items.push({ label: 'Size', value: s, remove: () => filters.sizes.delete(s) }),
  )
  if (filters.priceMin > 0 || filters.priceMax < priceMaxCap) {
    items.push({
      label: 'Price',
      value: `${formatPrice(filters.priceMin)} - ${formatPrice(filters.priceMax)}`,
      remove: () => {
        filters.priceMin = 0
        filters.priceMax = priceMaxCap
      },
    })
  }

  return (
    <div className="mig-marketplace-refine">
      <div className="mig-marketplace-refine-header">
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-controls="mig-marketplace-filters-collapsible"
          className="mig-marketplace-refine-toggle"
        >
          {/* Chevron only shows on mobile via CSS — desktop hides it. */}
          <span className="mig-marketplace-refine-chevron" aria-hidden="true">
            <ChevronDown open={mobileOpen} />
          </span>
          <span className="mig-marketplace-refine-toggle-label">Refine By</span>
        </button>
        {items.length > 0 && (
          <button onClick={onClearAll} className="mig-marketplace-clear-btn">
            Clear All
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mig-marketplace-refine-body">No filters applied.</p>
      ) : (
        <div className="mig-marketplace-refine-body mig-marketplace-refine-body--list">
          {items.map((it, idx) => (
            <div key={`${it.label}-${it.value}-${idx}`} className="mig-marketplace-refine-chip">
              <p>
                <span className="mig-marketplace-refine-chip-label">{it.label}: </span>
                <span className="mig-marketplace-refine-chip-value">{it.value}</span>
              </p>
              <button
                aria-label={`Remove ${it.label}: ${it.value}`}
                onClick={() => onRemove(it.remove)}
                className="mig-marketplace-refine-chip-remove"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#000000" strokeWidth="2" aria-hidden="true">
                  <path d="M5 5 L15 15 M15 5 L5 15" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ---------- Expandable Filter Category ---------- */
function FilterCategory({
  title,
  open,
  setOpen,
  canClear,
  onClear,
  children,
}: {
  title: string
  open: boolean
  setOpen: (v: boolean) => void
  canClear: boolean
  onClear: () => void
  children?: React.ReactNode
}) {
  return (
    <div className="mig-marketplace-filter-category">
      <div className="mig-marketplace-filter-category-header">
        <button
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="mig-marketplace-filter-category-toggle"
        >
          <ChevronDown open={open} />
          <span className="mig-marketplace-filter-category-title">{title}</span>
        </button>
        {canClear && (
          <button onClick={onClear} className="mig-marketplace-filter-category-clear">
            Clear
          </button>
        )}
      </div>
      {open && (
        <div className="mig-marketplace-filter-category-body">
          {children}
        </div>
      )}
    </div>
  )
}

/* ---------- Checkbox Row ---------- */
function CheckboxRow({
  label,
  count,
  checked,
  onToggle,
}: {
  label: string
  count?: number
  checked: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      aria-pressed={checked}
      className="mig-marketplace-checkbox-row"
    >
      <span className="mig-marketplace-checkbox-row-label">
        <Checkbox checked={checked} />
        <span className="mig-marketplace-checkbox-row-label-text">{label}</span>
      </span>
      {count != null && (
        <span className="mig-marketplace-checkbox-row-count">({count})</span>
      )}
    </button>
  )
}

/* ---------- Price Range ---------- */
function PriceRange({
  priceMaxCap,
  priceMin,
  priceMax,
  onChange,
}: {
  priceMaxCap: number
  priceMin: number
  priceMax: number
  onChange: (min: number, max: number) => void
}) {
  const [minInput, setMinInput] = useState(String(priceMin))
  const [maxInput, setMaxInput] = useState(String(priceMax))

  // Keep inputs in sync when parent changes (e.g. Clear All)
  useEffect(() => setMinInput(String(priceMin)), [priceMin])
  useEffect(() => setMaxInput(String(priceMax)), [priceMax])

  const commit = (minS: string, maxS: string) => {
    let mi = parseFloat(minS)
    let ma = parseFloat(maxS)
    if (Number.isNaN(mi)) mi = 0
    if (Number.isNaN(ma)) ma = priceMaxCap
    mi = Math.max(0, Math.min(priceMaxCap, mi))
    ma = Math.max(0, Math.min(priceMaxCap, ma))
    if (mi > ma) [mi, ma] = [ma, mi]
    onChange(mi, ma)
  }

  return (
    <div className="mig-marketplace-price-range">
      <div className="mig-marketplace-price-inputs">
        <label className="mig-marketplace-price-input-wrap">
          <input
            type="text"
            inputMode="numeric"
            value={minInput === '0' ? '$0' : `$${minInput}`}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, '')
              setMinInput(v || '0')
            }}
            onFocus={(e) => { setMinInput(minInput === '0' ? '' : minInput); e.target.select() }}
            onBlur={() => { if (!minInput) setMinInput('0'); commit(minInput || '0', maxInput) }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur() }}
            aria-label="Minimum price"
            className="mig-marketplace-price-input"
          />
        </label>
        <span className="mig-marketplace-price-dash">-</span>
        <label className="mig-marketplace-price-input-wrap">
          <input
            type="text"
            inputMode="numeric"
            value={`$${maxInput}`}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, '')
              setMaxInput(v || '0')
            }}
            onFocus={(e) => e.target.select()}
            onBlur={() => commit(minInput, maxInput)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur() }}
            aria-label="Maximum price"
            className="mig-marketplace-price-input"
          />
        </label>
      </div>

      {/* Range slider: dual-input approach (two sliders stacked).
          The active-segment position is driven by two CSS custom
          properties set inline; all layout lives in
          `.mig-marketplace-price-slider-active` in marketplace.css. */}
      <div className="mig-marketplace-price-slider">
        <div className="mig-marketplace-price-slider-track" />
        <div
          className="mig-marketplace-price-slider-active"
          style={{
            '--mig-range-left': `${(priceMin / priceMaxCap) * 100}%`,
            '--mig-range-right': `${100 - (priceMax / priceMaxCap) * 100}%`,
          } as React.CSSProperties}
        />
        <input
          type="range"
          min={0}
          max={priceMaxCap}
          step={50}
          value={priceMin}
          onChange={(e) => {
            const v = Math.min(parseFloat(e.target.value), priceMax)
            onChange(v, priceMax)
          }}
          aria-label="Minimum price slider"
          className="mig-marketplace-range mig-marketplace-price-slider-input"
        />
        <input
          type="range"
          min={0}
          max={priceMaxCap}
          step={50}
          value={priceMax}
          onChange={(e) => {
            const v = Math.max(parseFloat(e.target.value), priceMin)
            onChange(priceMin, v)
          }}
          aria-label="Maximum price slider"
          className="mig-marketplace-range mig-marketplace-price-slider-input"
        />
      </div>
    </div>
  )
}

/* ---------- Artwork Card ---------- */
function ArtworkCard({ artwork }: { artwork: ArtworkListItem }) {
  const imageUrl =
    artwork.main_image?.versions?.large ||
    artwork.main_image?.versions?.card ||
    artwork.main_image?.url ||
    null
  const isSold = (artwork.product_status || '').toLowerCase() === 'sold'

  return (
    <a href={`/marketplace/${artwork.slug}`} className="mig-artwork-card">
      <div className="mig-artwork-card-image">
        {imageUrl ? (
          <img src={imageUrl} alt={artwork.name} loading="lazy" decoding="async" />
        ) : (
          <span className="mig-artwork-card-placeholder">Artwork</span>
        )}
        {isSold && (
          <div aria-hidden="true" className="mig-marketplace-card-sold">
            <div className="mig-marketplace-card-sold-text">SOLD</div>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="mig-artwork-card-info">
        <p className="mig-artwork-card-name">{artwork.name}</p>
        {artwork.artist_name && (
          <p className="mig-artwork-card-artist">{artwork.artist_name}</p>
        )}
        {artwork.price != null && (
          <p className="mig-artwork-card-price">
            {formatPrice(artwork.price)}
          </p>
        )}
      </div>
    </a>
  )
}

/* ========================= Main Page ========================= */
export default function Marketplace() {
  const data = usePageData<MarketplaceListPageData>()
  const artworks = data?.artworks ?? []
  const priceMaxCap = Math.max(data?.price_max ?? 2000, 100)

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  const iiaOptions = data?.filter_options ?? {}
  const options = useMemo(() => deriveOptions(artworks, iiaOptions), [artworks, iiaOptions])

  // Filters stored as a Set-based object; we bump a rev counter to trigger re-render
  // after in-place mutations (chip removal), which is simpler than immutable updates.
  const [filters] = useState<FilterState>(() => emptyFilters(priceMaxCap))
  const [rev, setRev] = useState(0)
  const bump = () => setRev((r) => r + 1)
  const [search, setSearch] = useState('')

  // Which categories are open (defaults: first 4 open to mirror the Figma).
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({
    artist: true,
    style: true,
    palette: true,
    mood: true,
    price: true,
    size: false,
    medium: false,
    orientation: false,
  })
  const setCat = (key: string, val: boolean) =>
    setOpenCats((prev) => ({ ...prev, [key]: val }))

  // Mobile-only master toggle: collapses the whole filter structure
  // (active chips + every category section) when the user clicks the
  // "Refine By" chevron. Default closed on mobile so the artwork grid
  // is visible above the fold; desktop ignores this state because the
  // collapse CSS only fires inside the mobile media query.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const filtered = useMemo(
    () => filterArtworks(artworks, filters, search),
    // Depend on rev (bumped on every in-place mutation) so results update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artworks, search, rev, filters.priceMin, filters.priceMax],
  )

  const clearAll = () => {
    filters.artistIds.clear()
    filters.styles.clear()
    filters.palettes.clear()
    filters.moods.clear()
    filters.mediums.clear()
    filters.orientations.clear()
    filters.sizes.clear()
    filters.priceMin = 0
    filters.priceMax = priceMaxCap
    setSearch('')
    bump()
  }

  const toggleInSet = (set: Set<string>, v: string) => {
    if (set.has(v)) set.delete(v)
    else set.add(v)
    bump()
  }

  // Split artworks into rows of 3 (3-column grid per Figma)
  const rows: ArtworkListItem[][] = []
  for (let i = 0; i < filtered.length; i += 3) {
    rows.push(filtered.slice(i, i + 3))
  }

  return (
    <section className="mig-marketplace-section" data-visible={visible}>
      {/* Page header */}
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-marketplace-page-title">Marketplace</h1>
      </div>

      <div className="mig-marketplace-content">
        {/* ============================ Filters panel ============================ */}
        <div
          className="mig-marketplace-filters"
          role="search"
          aria-label="Marketplace filters"
        >
          <SearchBox value={search} onChange={setSearch} />
          <Divider />

          <RefineBy
            filters={filters}
            options={options}
            priceMaxCap={priceMaxCap}
            onRemove={(action) => { action(); bump() }}
            onClearAll={clearAll}
            mobileOpen={mobileFiltersOpen}
            setMobileOpen={setMobileFiltersOpen}
          />

          {/* Mobile collapsible region — every category and divider below
              the Refine By header lives in here so toggling the chevron
              hides the lot. Desktop CSS overrides display so the wrapper
              is fully transparent on larger screens. */}
          <div
            id="mig-marketplace-filters-collapsible"
            className="mig-marketplace-filters-collapsible"
            data-mobile-open={mobileFiltersOpen ? 'true' : 'false'}
          >
          <Divider />

          {/* Artist */}
          <FilterCategory
            title="Artist"
            open={openCats.artist}
            setOpen={(v) => setCat('artist', v)}
            canClear={filters.artistIds.size > 0}
            onClear={() => { filters.artistIds.clear(); bump() }}
          >
            {options.artists.map((a) => (
              <CheckboxRow
                key={a.id}
                label={a.name}
                count={a.count}
                checked={filters.artistIds.has(a.id)}
                onToggle={() => toggleInSet(filters.artistIds, a.id)}
              />
            ))}
            {options.artists.length === 0 && (
              <p className="mig-marketplace-filter-empty">No artists yet.</p>
            )}
          </FilterCategory>
          <Divider />

          {/* Style — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Style"
            open={openCats.style}
            setOpen={(v) => setCat('style', v)}
            canClear={filters.styles.size > 0}
            onClear={() => { filters.styles.clear(); bump() }}
          >
            {options.styles.map((s) => (
              <CheckboxRow
                key={s}
                label={s}
                checked={filters.styles.has(s)}
                onToggle={() => toggleInSet(filters.styles, s)}
              />
            ))}
            {options.styles.length === 0 && (
              <p className="mig-marketplace-filter-empty">—</p>
            )}
          </FilterCategory>
          <Divider />
          */}

          {/* Palette — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Palette"
            open={openCats.palette}
            setOpen={(v) => setCat('palette', v)}
            canClear={filters.palettes.size > 0}
            onClear={() => { filters.palettes.clear(); bump() }}
          >
            {options.palettes.map((p) => (
              <CheckboxRow
                key={p}
                label={p}
                checked={filters.palettes.has(p)}
                onToggle={() => toggleInSet(filters.palettes, p)}
              />
            ))}
            {options.palettes.length === 0 && (
              <p className="mig-marketplace-filter-empty">—</p>
            )}
          </FilterCategory>
          <Divider />
          */}

          {/* Mood — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Mood"
            open={openCats.mood}
            setOpen={(v) => setCat('mood', v)}
            canClear={filters.moods.size > 0}
            onClear={() => { filters.moods.clear(); bump() }}
          >
            {options.moods.map((m) => (
              <CheckboxRow
                key={m}
                label={m}
                checked={filters.moods.has(m)}
                onToggle={() => toggleInSet(filters.moods, m)}
              />
            ))}
            {options.moods.length === 0 && (
              <p className="mig-marketplace-filter-empty">—</p>
            )}
          </FilterCategory>
          <Divider />
          */}

          {/* Price — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Price"
            open={openCats.price}
            setOpen={(v) => setCat('price', v)}
            canClear={filters.priceMin > 0 || filters.priceMax < priceMaxCap}
            onClear={() => { filters.priceMin = 0; filters.priceMax = priceMaxCap; bump() }}
          >
            <PriceRange
              priceMaxCap={priceMaxCap}
              priceMin={filters.priceMin}
              priceMax={filters.priceMax}
              onChange={(mi, ma) => { filters.priceMin = mi; filters.priceMax = ma; bump() }}
            />
          </FilterCategory>
          <Divider />
          */}

          {/* Size — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Size"
            open={openCats.size}
            setOpen={(v) => setCat('size', v)}
            canClear={filters.sizes.size > 0}
            onClear={() => { filters.sizes.clear(); bump() }}
          >
            {options.sizes.map((s) => (
              <CheckboxRow
                key={s}
                label={s}
                checked={filters.sizes.has(s)}
                onToggle={() => toggleInSet(filters.sizes, s)}
              />
            ))}
            {options.sizes.length === 0 && (
              <p className="mig-marketplace-filter-empty">—</p>
            )}
          </FilterCategory>
          <Divider />
          */}

          {/* Medium — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Medium"
            open={openCats.medium}
            setOpen={(v) => setCat('medium', v)}
            canClear={filters.mediums.size > 0}
            onClear={() => { filters.mediums.clear(); bump() }}
          >
            {options.mediums.map((m) => (
              <CheckboxRow
                key={m}
                label={m}
                checked={filters.mediums.has(m)}
                onToggle={() => toggleInSet(filters.mediums, m)}
              />
            ))}
            {options.mediums.length === 0 && (
              <p className="mig-marketplace-filter-empty">—</p>
            )}
          </FilterCategory>
          <Divider />
          */}

          {/* Orientation — hidden per June UAT feedback: only the Artist filter is shown
          <FilterCategory
            title="Orientation"
            open={openCats.orientation}
            setOpen={(v) => setCat('orientation', v)}
            canClear={filters.orientations.size > 0}
            onClear={() => { filters.orientations.clear(); bump() }}
          >
            {options.orientations.map((o) => (
              <CheckboxRow
                key={o}
                label={o}
                checked={filters.orientations.has(o)}
                onToggle={() => toggleInSet(filters.orientations, o)}
              />
            ))}
            {options.orientations.length === 0 && (
              <p className="mig-marketplace-filter-empty">—</p>
            )}
          </FilterCategory>
          <Divider />
          */}
          </div>
        </div>

        {/* ============================ Artwork grid ============================ */}
        <div className="mig-marketplace-grid-wrapper">
          {/* Top divider above grid - 12px thick per Figma */}
          <div className="mig-divider-thick-12" />

          {/* Results summary */}
          <p className="mig-marketplace-count">
            {filtered.length === artworks.length
              ? `Showing all ${filtered.length} artwork${filtered.length === 1 ? '' : 's'}`
              : `Showing ${filtered.length} of ${artworks.length} artworks`}
          </p>

          {filtered.length === 0 ? (
            <p className="mig-marketplace-empty">
              No artworks match these filters. Try adjusting or clearing them.
            </p>
          ) : (
            <div className="mig-marketplace-grid">
              {rows.map((row, rowIdx) => (
                <div key={rowIdx} className="mig-marketplace-row">
                  {row.map((a) => (
                    <ArtworkCard key={a.id} artwork={a} />
                  ))}
                  {/* Pad the last row so alignment stays intact */}
                  {Array.from({ length: 3 - row.length }).map((_, i) => (
                    <div key={`pad-${i}`} className="mig-marketplace-row-spacer" aria-hidden="true" />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
